import React, { useEffect, useState } from 'react';

// Иконка щита (SVG)
const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16 text-blue-500 mb-6 drop-shadow-lg">
    <path fillRule="evenodd" d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.07-7.734-3.08zM12 13.25a.75.75 0 000-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
    <path d="M12 15.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
  </svg>
);

// Иконка документа
const DocIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-500">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);

const AgreementGuard = ({ children }) => {
    const [isChecking, setIsChecking] = useState(true);
    const [needsAgreement, setNeedsAgreement] = useState(false);
    const [legalDocs, setLegalDocs] = useState([]);
    
    // Состояние для "Шторки" (Bottom Sheet)
    const [activeDocId, setActiveDocId] = useState(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const tg = window.Telegram?.WebApp;
    const userId = tg?.initDataUnsafe?.user?.id;

    // --- CHANGED: Функция очистки заголовка (Убирает "1.", "2." и пробелы) ---
    const cleanTitle = (title) => {
        if (!title) return "";
        // Регулярное выражение: ищет цифры в начале, за которыми идет точка или скобка
        return title.replace(/^\d+[.)]\s*/, '');
    };

    useEffect(() => {
        if (!userId) {
            setIsChecking(false);
            return;
        }

        Promise.all([
            fetch(`/api/profile?user_id=${userId}`).then(r => r.json()),
            fetch('/api/legal-documents').then(r => r.json()).catch(() => [])
        ]).then(([profile, docs]) => {
            if (profile.is_agreed === false) {
                setNeedsAgreement(true);
                
                // Фильтруем документы
                const requiredDocs = docs.filter(doc => {
                    const t = doc.title.toLowerCase();
                    return (t.includes('условия') || t.includes('оферта')) || 
                           (t.includes('политика') || t.includes('конфиденциальности'));
                });
                setLegalDocs(requiredDocs);
            }
        }).catch(err => {
            console.error("Agreement check failed:", err);
        }).finally(() => {
            setIsChecking(false);
        });
    }, [userId]);

    const handleAccept = async () => {
        try {
            const res = await fetch('/api/accept-agreement', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    user_data: tg?.initDataUnsafe?.user,
                    start_param: tg?.initDataUnsafe?.start_param 
                })
            });
            
            if (res.ok) {
                if (tg.HapticFeedback) tg.HapticFeedback.notificationOccurred('success');
                setNeedsAgreement(false);
            }
        } catch (e) {
            console.error("Failed to accept", e);
            tg?.showAlert("Ошибка соединения. Попробуйте позже.");
        }
    };

    const openDoc = (id) => {
        setActiveDocId(id);
        setTimeout(() => setIsSheetOpen(true), 10);
    };

    const closeDoc = () => {
        setIsSheetOpen(false);
        setTimeout(() => setActiveDocId(null), 300);
    };

    if (isChecking) {
        return (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-tg-bg text-tg-text">
                <div className="animate-pulse font-medium opacity-60">Загрузка...</div>
            </div>
        );
    }

    if (!needsAgreement) return children;

    const activeDocContent = activeDocId ? legalDocs.find(d => d.id === activeDocId) : null;

    return (
        <div className="fixed inset-0 z-[9990] flex items-end sm:items-center justify-center bg-tg-bg overflow-hidden">
            
            {/* 1. Главный экран */}
            <div className="w-full h-full max-w-md mx-auto flex flex-col p-6 animate-fade-in">
                
                <div className="flex-1 flex flex-col items-center justify-center text-center mt-10">
                    <ShieldIcon />
                    
                    <h1 className="text-2xl font-bold text-tg-text mb-3">
                        Безопасность данных
                    </h1>
                    
                    <p className="text-tg-hint text-base leading-relaxed max-w-[280px]">
                        Для продолжения работы мы просим вас ознакомиться и принять юридические документы.
                    </p>
                </div>

                {/* Блок с документами */}
                <div className="w-full space-y-3 mb-8">
                    {legalDocs.map(doc => (
                        <button 
                            key={doc.id} 
                            onClick={() => openDoc(doc.id)} 
                            className="w-full flex items-center justify-between p-4 bg-tg-secondary-bg rounded-2xl active:scale-[0.98] transition-all duration-200 group"
                        >
                            <div className="flex items-center space-x-4">
                                <div className="p-2 bg-tg-bg rounded-lg group-active:bg-blue-500/10 transition-colors">
                                    <DocIcon />
                                </div>
                                {/* CHANGED: Применяем cleanTitle для отображения */}
                                <span className="text-left font-medium text-tg-text text-sm">
                                    {cleanTitle(doc.title)}
                                </span>
                            </div>
                            <span className="text-tg-hint text-xl opacity-50">›</span>
                        </button>
                    ))}
                </div>

                <div className="mb-4">
                    <button 
                        onClick={handleAccept}
                        className="w-full py-4 bg-blue-500 text-white rounded-2xl font-bold text-lg shadow-xl shadow-blue-500/20 active:scale-95 transition-transform"
                    >
                        Принять и продолжить
                    </button>
                    <p className="text-center text-xs text-tg-hint mt-4 opacity-60">
                        Нажимая кнопку, вы подтверждаете свое согласие
                    </p>
                </div>
            </div>

            {/* 2. Шторка (Bottom Sheet) */}
            {activeDocId && (
                <div className="fixed inset-0 z-[9999] flex justify-center items-end">
                    
                    <div 
                        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${isSheetOpen ? 'opacity-100' : 'opacity-0'}`}
                        onClick={closeDoc}
                    ></div>

                    <div 
                        className={`bg-tg-bg w-full max-w-lg h-[85vh] rounded-t-[30px] flex flex-col shadow-2xl transition-transform duration-300 ease-out transform ${isSheetOpen ? 'translate-y-0' : 'translate-y-full'}`}
                    >
                        <div className="w-full flex justify-center pt-3 pb-1" onClick={closeDoc}>
                            <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full opacity-50"></div>
                        </div>

                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                            {/* CHANGED: Применяем cleanTitle и в заголовке шторки */}
                            <h2 className="text-lg font-bold text-tg-text truncate pr-4">
                                {cleanTitle(activeDocContent?.title)}
                            </h2>
                            <button 
                                onClick={closeDoc}
                                className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-tg-hint hover:text-tg-text transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            <div 
                                className="prose dark:prose-invert prose-sm max-w-none text-tg-text leading-relaxed"
                                dangerouslySetInnerHTML={{ 
                                    __html: activeDocContent?.content || '<p class="text-center opacity-50">Загрузка...</p>' 
                                }} 
                            />
                            <div className="h-10"></div>
                        </div>

                        <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-tg-bg pb-8">
                            <button 
                                onClick={closeDoc}
                                className="w-full py-3 bg-tg-secondary-bg text-tg-text font-semibold rounded-xl active:opacity-70 transition-opacity"
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgreementGuard;
